import { toast } from '@heroui/react'
import { create } from 'zustand'

import type { GetDriversParams } from '@/api/drivers.api'
import { driversApi } from '@/api/drivers.api'
import i18n from '@/i18n'
import type { CreateDriverRequest, Driver as ApiDriver, UpdateDriverRequest } from '@/types/driver'
import type { Driver as UiDriver } from '@/types/driver.types'
import { getAppSettings, updateAppSettings } from '@/utils/settings'
import { SortDirection, sorting, type TableSort } from '@/utils/sorting'

interface DriversStore {
  drivers: UiDriver[];
  isLoading: boolean;
  error: string | null;
  filters: {
    name: string;
    phone: string;
  };
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  fetchDrivers: () => Promise<void>;
  addDriver: (d: Omit<UiDriver, 'id'>) => Promise<UiDriver>;
  updateDriver: (id: string, patch: Partial<Omit<UiDriver, 'id'>>) => Promise<void>;
  deleteDriver: (id: string) => Promise<void>;

  setFilters: (filters: Partial<DriversStore['filters']>) => void;
  setPagination: (pagination: Partial<DriversStore['pagination']>) => void;
  resetFilters: () => void;
  clearError: () => void;
}

const mapApiToUi = (d: ApiDriver): UiDriver => ({
  id: d.ID,
  fullName: d.Name,
  phone: d.Phone,
  assignedVehicleId: d.Vehicle?.ID ?? undefined,
  assignedVehicle: d.Vehicle ? {
    id: d.Vehicle.ID,
    plate: d.Vehicle.Number,
    brand: d.Vehicle.Model,
    year: d.Vehicle.Make,
    active: true,
  } : null,
  active: true, // Defaulting to true as in vehicles
})

const mapUiToApiCreate = (d: Omit<UiDriver, 'id'>): CreateDriverRequest => ({
  Name: d.fullName,
  Phone: d.phone ?? '',
  VehicleID: d.assignedVehicleId === 'none' ? null : (d.assignedVehicleId ?? null),
})

const mapUiToApiUpdate = (d: Partial<Omit<UiDriver, 'id'>>): UpdateDriverRequest => ({
  Name: d.fullName,
  Phone: d.phone,
  VehicleID: d.assignedVehicleId === 'none' ? null : (d.assignedVehicleId ?? undefined),
})

export const useDriversStore = create<DriversStore>((set, get) => ({
  drivers: [],
  isLoading: false,
  error: null,
  filters: {
    name: '',
    phone: '',
  },
  pagination: {
    sort: { key: 'name', direction: SortDirection.ASC },
    page: 0,
    limit: getAppSettings().driversLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchDrivers: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params: GetDriversParams = {
      name: filters.name || undefined,
      phone: filters.phone || undefined,
      page: pagination.page,
      limit: pagination.limit,
      sort: pagination.sort ? [sorting(pagination.sort)] : undefined,
    }

    try {
      const response = await driversApi.getDrivers(params)
      set({
        drivers: response.Drivers.map(mapApiToUi),
        pagination: {
          ...pagination,
          totalPages: Math.ceil(response.Pagination.Total / pagination.limit),
          total: response.Pagination.Total,
        },
      })
    } catch (err) {
      const msg = (err as Error).message
      set({ error: msg })
      toast.danger(i18n.t('drivers.toast.fetch_error'), { description: msg })
    } finally {
      set({ isLoading: false })
    }
  },

  addDriver: async d => {
    set({ isLoading: true, error: null })
    try {
      const driver = await driversApi.createDriver(mapUiToApiCreate(d))
      const uiDriver = mapApiToUi(driver)
      toast.success(i18n.t('drivers.toast.create_success'), { description: i18n.t('drivers.toast.create_success_desc') })

      await get().fetchDrivers()

      return uiDriver
    } catch (err) {
      toast.danger(i18n.t('drivers.toast.create_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateDriver: async (id, patch) => {
    set({ isLoading: true, error: null })
    try {
      await driversApi.updateDriver(id, mapUiToApiUpdate(patch))
      toast.success(i18n.t('drivers.toast.update_success'), { description: i18n.t('drivers.toast.update_success_desc') })

      await get().fetchDrivers()
    } catch (err) {
      toast.danger(i18n.t('drivers.toast.update_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deleteDriver: async id => {
    set({ isLoading: true, error: null })
    try {
      await driversApi.deleteDriver(id)
      toast.success(i18n.t('drivers.toast.delete_success'), { description: i18n.t('drivers.toast.delete_success_desc') })

      await get().fetchDrivers()
    } catch (err) {
      toast.danger(i18n.t('drivers.toast.delete_error'), { description: (err as Error).message })
      set({ error: (err as Error).message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchDrivers()
  },

  setPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.pagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ driversLimit: newPagination.limit })
      }
      return { pagination: updatedPagination }
    })
    get().fetchDrivers()
  },

  resetFilters: () => {
    const initialLimit = getAppSettings().driversLimit ?? 10
    set({
      filters: {
        name: '',
        phone: '',
      },
      pagination: {
        sort: { key: 'name', direction: SortDirection.ASC },
        page: 0,
        limit: initialLimit,
        totalPages: 1,
        total: 0,
      },
    })
    get().fetchDrivers()
  },

  clearError: () => set({ error: null }),
}))
